import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import ECTSCalculatorComponent from "./EctsCalculatorComponent";
import EducationModalContainer from "./educationalModal/EducationModalContainer";
import LogContainer from "../../common/components/LogContainer";
import GPARedirect from "./components/GPARedirect";
import PhoneVerificationModal from "../../common/components/PhoneVerificationModal";
import LoginActions from "../../login/duck/actions";
import protectedAxios from "../../common/axios";
import { API_URL } from "../../common/constants";
import { createAppUrlObject } from "../common/utils";
import useSubscription from "../common/hooks/useSubscription";
import useListings from "../common/hooks/useListing";
import useToolLeadCreation from "../../common/hooks/useToolLeadCreation";
import Footer from "../../static-pages/common/Footer";

const TOOL_SOURCE_NAME = "ECTS Calculator";

export default function ECTSCalculatorContainer() {
  let { url } = useRouteMatch();
  url = window.location.search ? url + window.location.search : url;
  const dispatch = useDispatch();

  const [lectureHours, setLectureHours] = useState(null);
  const [selfStudyHours, setSelfStudyHours] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [validEctsValues, setValidEctsValues] = useState(null);
  const [ectsScore, setEctsScore] = useState(null);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [programIds, setProgramIds] = useState([]);
  const [enableSaveButton, setEnableSaveButton] = useState(true);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Holds the pending calculation while we wait for phone verification
  const [pendingCalculation, setPendingCalculation] = useState(false);

  const appUrl = createAppUrlObject();
  const [listing] = useListings(appUrl);
  const [subscription, setSubscription] = useSubscription(listing?._id);

  const slug = "ects-calculator";

  const isUserAuthenticated = useSelector(
    (state) => state.user.isAuthenticated
  );
  const userProfile = useSelector((state) => state.user?.profile);
  const loggedInUserProfile = useSelector(
    (state) => state.user?.gamePlayerProfile
  );

  const { ensureLead } = useToolLeadCreation(TOOL_SOURCE_NAME);

  /**
   * Check whether the user has a verified phone number.
   * SMS/WhatsApp logins always have a phone (+ fake email), so this only
   * gates email/OAuth users who are missing a phone.
   */
  const hasVerifiedPhone = useCallback(() => {
    return !!(userProfile?.phone && userProfile?.isPhoneVerified);
  }, [userProfile]);

  /**
   * Core calculation logic — separated so it can be called after phone
   * verification completes.
   */
  const executeCalculation = useCallback(() => {
    if (lectureHours == 0 || weeks == 0) {
      toast.error("Enter Valid Values");
      return;
    }

    if (parseFloat(lectureHours) + parseFloat(selfStudyHours) > 168) {
      toast.error("Lecture and Self Study Hours to large");
      return;
    }

    if (parseFloat(weeks) > 26) {
      toast.error("Weeks per Semester cannot be greater than 26");
      return;
    }

    const credits = (lectureHours * weeks + selfStudyHours * weeks) / 30;
    setValidEctsValues({ lectureHours, selfStudyHours, weeks });
    setEctsScore(credits.toFixed(2));
    setProgramIds(getInitialProgramIds(loggedInUserProfile));
    createSkyliftSubscription();

    // Fire-and-forget lead creation — don't block the result
    ensureLead();
  }, [lectureHours, selfStudyHours, weeks, loggedInUserProfile]);

  // When phone is verified via the modal, execute the pending calculation
  const handlePhoneVerified = useCallback(() => {
    if (pendingCalculation) {
      setPendingCalculation(false);
      // Small delay to let Redux profile update propagate
      setTimeout(() => executeCalculation(), 300);
    }
  }, [pendingCalculation, executeCalculation]);

  useEffect(() => {
    async function save() {
      const newProfile = await getNewUserProfile();
      const newProgramIdsArray = getInitialProgramIds(newProfile);
      const differenceArray = newProgramIdsArray?.filter((x) => {
        return programIds.indexOf(x) < 0;
      });
      if (differenceArray?.length) {
        setEnableSaveButton(false);
        const payload = {
          programId: differenceArray[0],
          lectureHours: validEctsValues.lectureHours,
          selfStudyHours: validEctsValues.selfStudyHours,
          weeks: validEctsValues.weeks,
        };

        protectedAxios
          .post(`${API_URL}/v1/ects/save-score`, payload)
          .then(() => {
            toast.success("ECTS Score Saved!");
            setEnableSaveButton(true);
            setProgramIds(newProgramIdsArray);
          })
          .catch(() => {
            toast.error("Failed to Save Score!");
            setEnableSaveButton(true);
            setProgramIds(newProgramIdsArray);
          });
      }
    }

    if (validEctsValues) {
      save();
    }
  }, [showEducationModal]);

  async function getNewUserProfile() {
    const response = await protectedAxios.get(
      `${API_URL}/v1/game-player/profile/${loggedInUserProfile.userName}?gameplayerId=${loggedInUserProfile.gamePlayerId}`
    );
    return response.data;
  }

  const toggleModal = () => {
    setShowEducationModal(!showEducationModal);
  };

  const calculateEcts = () => {
    // Step 1: Must be logged in
    if (!isUserAuthenticated) {
      return dispatch(
        LoginActions.showLoginModal(true, {
          redirectTo: url,
          isLoginDismissible: true,
        })
      );
    }

    // Step 2: Must have a verified phone number
    if (!hasVerifiedPhone()) {
      setPendingCalculation(true);
      setShowPhoneModal(true);
      return;
    }

    // Step 3: Execute calculation + lead creation
    executeCalculation();
  };

  const saveUserScore = () => {
    if (!isUserAuthenticated) {
      return dispatch(
        LoginActions.showLoginModal(true, {
          redirectTo: url,
          isLoginDismissible: true,
        })
      );
    }
    toggleModal();
  };

  const createSkyliftSubscription = () => {
    // If Listings in available and subscription state is not 3 (Payment completed)
    if (subscription?.state !== 3 && Object.keys(listing).length) {
      const payload = {
        listing: {
          ...listing,
          category: listing.category?._id, // Send only category id
        },
        state: 3, // Payment completed state
      };

      protectedAxios
        .post(`${API_URL}/v1/gigs/subscription/app`, payload)
        .then(({ data }) => {
          setSubscription(data?.gigOrder);
        })
        .catch((error) => console.error(error));
    }
  };

  function getInitialProgramIds(userProfile) {
    let programArr = [];
    const associatedPrograms = userProfile.associatedPrograms;
    const domesticPrograms = userProfile.domesticPrograms;
    associatedPrograms?.enrolled?.map((program) => {
      programArr.push(program._id);
    });
    associatedPrograms?.alumnus?.map((program) => {
      programArr.push(program._id);
    });
    domesticPrograms?.enrolled?.map((program) => {
      programArr.push(program._id);
    });
    domesticPrograms?.alumnus?.map((program) => {
      programArr.push(program._id);
    });
    return programArr;
  }

  return (
    <LogContainer eventName="page-visit" slug={slug} logImpression>
      <ECTSCalculatorComponent
        lectureHours={lectureHours}
        selfStudyHours={selfStudyHours}
        weeks={weeks}
        ectsScore={ectsScore}
        setLectureHours={setLectureHours}
        setSelfStudyHours={setSelfStudyHours}
        setWeeks={setWeeks}
        calculateEcts={calculateEcts}
        saveUserScore={saveUserScore}
        enableSaveButton={enableSaveButton}
      />
      <EducationModalContainer
        showEducationModal={showEducationModal}
        setShowEducationModal={setShowEducationModal}
        validEctsValues={validEctsValues}
      />
      <PhoneVerificationModal
        showModal={showPhoneModal}
        setShowModal={setShowPhoneModal}
        onVerified={handlePhoneVerified}
        title="Verify your phone number"
        subtitle="We need your phone number to share your personalised ECTS credit report."
      />
      <GPARedirect path={"germany-grade-calculator"} />
      <Footer />
    </LogContainer>
  );
}
